<template>
	<div
		v-if="!hasError"
		class="position-relative"
		@mouseenter="isHovered = true"
		@mouseleave="isHovered = false"
	>
		<div
			id="hideinmobile"
			v-show="isHovered && canScrollLeft"
			class="aimock-interview-arws left"
			@click.stop="scrollCards('left')"
		>
			<img src="../../../assets/NewDashboard/light-bluearrow.svg" alt="" />
		</div>
		<div
			id="hideinmobile"
			v-show="isHovered && canScrollRight"
			class="aimock-interview-arws right"
			@click.stop="scrollCards('right')"
		>
			<img src="../../../assets/NewDashboard/light-bluearrow.svg" alt="" />
		</div>

		<div
			ref="cardsScroll"
			class="d-flex align-items-center gap-16px mobpt-35px mobpt-30px pt-20px mobmt-8px w-100 overflow-x-auto overflow-y-hidden h-100 invisible-scroll ph-snap-scroll"
			@scroll.passive="onCardsScroll"
		>
			<!-- Loading skeleton -->
			<template v-if="isLoading">
				<div class="ph-maincard ph-snap-item" v-for="i in 3" :key="'sk-' + i">
					<div
						class="ph-maincard-tag d-flex align-items-center"
						style="background: transparent; border: none; padding: 0"
					>
						<b-skeleton
							width="100px"
							height="20px"
							style="border-radius: 4px"
						></b-skeleton>
					</div>
					<div class="d-flex align-items-center gap-10px">
						<b-skeleton
							type="avatar"
							size="40px"
							class="flex-shrink-0"
							style="border-radius: 8px"
						></b-skeleton>
						<b-skeleton width="90px" height="14px"></b-skeleton>
					</div>
					<section class="mt-8px">
						<b-skeleton width="70%" height="15px" class="mb-2"></b-skeleton>
						<b-skeleton width="50%" height="13px"></b-skeleton>
					</section>
				</div>
			</template>

			<!-- Actual cards -->
			<template v-else>
				<div
					class="ph-maincard ph-snap-item"
					v-for="(card, idx) in cards"
					:key="card.id || idx"
					@click="onCardClick(card)"
					style="cursor: pointer"
				>
					<!-- tag part starts (so bind the class 'ph-coursestag' fr asmntcard n coursecard ) -->
					<div
						class="ph-maincard-tag"
						:class="
							card.type === 'job'
								? 'ph-jobapllicationtag ph-othertags'
								: 'ph-coursestag ph-othertags'
						"
					>
						<span v-if="card.type === 'job'">
							<template v-if="card.eventAt">
								Interview on <b>{{ formatEventDate(card.eventAt) }}</b>
							</template>
							<template v-else>
								{{ card.status }}
							</template>
						</span>
						<span v-else-if="card.type === 'assessment'">
							Attempts: <b>{{ card.attemptsText }}</b>
						</span>
						<span v-else-if="card.type === 'course'">
							{{ Math.round(card.progressPercentage) }}% Done
						</span>
					</div>
					<!-- tag part ends -->

					<div class="d-flex align-items-center gap-10px">
						<img
							class="progressicon-shadow"
							:src="cardIcon(card.type)"
							:alt="cardTypeLabel(card.type)"
						/>
						<span class="fam-mont fw-LM fs-12 color-7a7a7a" id="hideinmobile">
							{{ cardTypeLabel(card.type) }}
						</span>
					</div>

					<section class="mt-8px">
						<div id="hideinlaptop" class="fam-mont fw-LM fs-12 color-7a7a7a">
							{{ cardTypeLabel(card.type) }}
						</div>

						<div
							class="mobmt-8px fam-mont fw-M fs-14 color-0F172A text-truncate text-capitalize"
						>
							{{ card.title }}
						</div>

						<!-- Job subtitle -->
						<div
							v-if="card.type === 'job'"
							:ref="'jobSubtitle-' + subtitleKey(card, idx)"
							class="mobmt-8px fam-inter fw-R fs-12 color-64748B text-capitalize ph-subtitle"
						>
							<span
								class="ph-subtitle-item"
								:style="jobFieldStyle(card, idx, 'company')"
							>
								{{ card.companyName }}
							</span>

							<span class="ph-subtitle-bullet">•</span>

							<span
								class="ph-subtitle-item"
								:style="jobFieldStyle(card, idx, 'jobType')"
							>
								{{ card.jobType }}
							</span>

							<span class="ph-subtitle-bullet">•</span>

							<span
								class="ph-subtitle-item"
								:style="jobFieldStyle(card, idx, 'location')"
							>
								{{ card.location }}
							</span>
						</div>

						<!-- Assessment subtitle -->
						<div
							v-else-if="card.type === 'assessment'"
							class="mobmt-8px fam-inter fw-R fs-12 color-64748B text-truncate"
						>
							<span>Level {{ card.difficultyLevel }}</span>
							<template v-if="card.durationMinutes">
								•
								<span>{{
									formatDuration(card.durationMinutes)
								}}</span></template
							>
						</div>

						<!-- Course subtitle -->
						<div
							v-else-if="card.type === 'course'"
							class="mobmt-8px fam-inter fw-R fs-12 color-64748B text-truncate"
						>
							<span>{{ card.category }}</span> •
							<span>by {{ card.authorName }}</span>
						</div>
					</section>
				</div>

				<!-- Empty state -->
				<!-- <div v-if="!isLoading && cards.length === 0" class="ph-empty-state fam-mont fw-R fs-13 color-7a7a7a">
					No activity to show right now.
				</div> -->
			</template>
		</div>

		<!-- sliding dot indicator — mobile only -->
		<div
			id="hideinlaptop"
			class="ph-dot-viewport"
			v-if="!isLoading && cards.length > 1"
		>
			<div class="ph-dot-track" :style="dotTrackStyle">
				<span
					v-for="(_, i) in cards"
					:key="i"
					:class="[
						'ph-dot',
						i === activeCardIndex ? 'ph-dot--active' : 'ph-dot--small',
					]"
				></span>
			</div>
		</div>

		<!-- Test details modal (laptop). On mobile we route away instead. -->
		<TestDetails
			v-if="showTestDetails"
			:showTestDetails="showTestDetails"
			:selectedAssessment="selectedAssessment"
			:public="isPublic"
			@closeTestModal="showTestDetails = false"
		/>
	</div>
</template>

<script>
import { getProgressHubData, getAssessmentDetail } from "@/utils/apiFunctions";
import { config } from "@/config";
import itemTrackingMixin from "@/tracking/mixins/itemTrackingMixin";
import TestDetails from "@/views/AssessmentProgress/TestDetailsAP.vue";
import EventBus from "@/utils/EventBus";
const CARD_EVENT = {
	job: { key: "job_viewed", payload: (id) => ({ jobID: id }) },
	course: { key: "course_viewed", payload: (id) => ({ courseID: id }) },
	assessment: { key: "assessment_viewed", payload: (id) => ({ assessmentID: id }) },
};

export default {
	mixins: [itemTrackingMixin],
	components: {
		TestDetails,
	},
	data() {
		return {
			isHovered: false,
			activeCardIndex: 0,
			isLoading: true,
			cards: [],
			canScrollLeft: false,
			canScrollRight: false,
			hasError: false,
			isMobile: false,
			showTestDetails: false,
			selectedAssessment: null,
			isPublic: false,
			jobSubtitleWidths: {},
		};
	},
	computed: {
		dotTrackStyle() {
			const DOT = 8;
			const PILL = 19;
			const GAP = 8;
			const VIEWPORT = DOT + GAP + PILL + GAP + DOT;
			const n = this.cards.length || 1;
			const idx = this.activeCardIndex;

			const trackWidth = PILL + (n - 1) * DOT + (n - 1) * GAP;
			const pillLeft = idx * DOT + idx * GAP;
			const pillCenter = pillLeft + PILL / 2;

			let translate = VIEWPORT / 2 - pillCenter;
			const minTranslate = VIEWPORT - trackWidth;
			const maxTranslate = 0;
			translate = Math.min(maxTranslate, Math.max(minTranslate, translate));

			return { transform: `translateX(${translate}px)` };
		},
	},
	mounted() {
		window.addEventListener("resize", this.calculateJobSubtitleWidths);
		window.addEventListener("resize", this.updateScrollArrows);
	},
	beforeDestroy() {
		window.removeEventListener("resize", this.updateScrollArrows);
		window.removeEventListener("resize", this.calculateJobSubtitleWidths);
		EventBus.$off("login-success", this.fetchProgressHub);
		EventBus.$off("onboarding-fetch-post-login", this.fetchProgressHub);
	},
	async created() {
		this.isMobile = window.innerWidth <= 1028;
		EventBus.$on("login-success", this.fetchProgressHub);
		EventBus.$on("onboarding-fetch-post-login", this.fetchProgressHub);
		await this.fetchProgressHub();
	},
	methods: {
		calculateJobSubtitleWidths() {
			this.$nextTick(() => {
				const widths = {};

				this.cards.forEach((card, index) => {
					if (card.type !== "job") return;

					const key = this.subtitleKey(card, index);
					const ref = this.$refs[`jobSubtitle-${key}`];
					const subtitle = Array.isArray(ref) ? ref[0] : ref;

					if (!subtitle) return;

					const totalWidth = subtitle.clientWidth;
					if (!totalWidth) return;

					const fields = ["company", "jobType", "location"];

					/*
					 * ---------------------------------------------------------
					 * 1. Calculate the actual space available for text.
					 * ---------------------------------------------------------
					 */

					let bulletWidth = 0;

					subtitle.querySelectorAll(".ph-subtitle-bullet").forEach((bullet) => {
						bulletWidth += bullet.getBoundingClientRect().width;
					});

					const gapWidth = 16;

					const availableWidth = totalWidth - bulletWidth - gapWidth;

					if (availableWidth <= 0) return;

					/*
					 * ---------------------------------------------------------
					 * 2. Starting allocation = 30 / 35 / 35.
					 * These are NOT maximum widths.
					 * ---------------------------------------------------------
					 */

					const base = {
						company: availableWidth * 0.3,
						jobType: availableWidth * 0.35,
						location: availableWidth * 0.35,
					};

					/*
					 * ---------------------------------------------------------
					 * 3. Measure the REAL natural width of each rendered field.
					 * ---------------------------------------------------------
					 */

					const natural = {};

					const spans = subtitle.querySelectorAll(".ph-subtitle-item");

					fields.forEach((field, i) => {
						const span = spans[i];

						if (!span) {
							natural[field] = 0;
							return;
						}

						const previousStyle = span.getAttribute("style");

						span.style.width = "max-content";
						span.style.minWidth = "0";
						span.style.flex = "0 0 auto";
						span.style.overflow = "visible";
						span.style.textOverflow = "clip";
						span.style.whiteSpace = "nowrap";

						natural[field] = Math.ceil(span.getBoundingClientRect().width);

						if (previousStyle === null) {
							span.removeAttribute("style");
						} else {
							span.setAttribute("style", previousStyle);
						}
					});

					/*
					 * ---------------------------------------------------------
					 * 4. Start everyone at their 30/35/35 allocation.
					 * ---------------------------------------------------------
					 */

					const result = {
						company: base.company,
						jobType: base.jobType,
						location: base.location,
					};

					/*
					 * ---------------------------------------------------------
					 * 5. SHORT fields give back their UNUSED space.
					 * ---------------------------------------------------------
					 */

					let donatedSpace = 0;

					fields.forEach((field) => {
						if (natural[field] < base[field]) {
							result[field] = natural[field];

							donatedSpace += base[field] - natural[field];
						}
					});

					/*
					 * ---------------------------------------------------------
					 * 6. Find fields that genuinely need extra space.
					 * ---------------------------------------------------------
					 */

					let receivers = fields.filter(
						(field) => natural[field] > base[field]
					);

					/*
					 * Keep 5px breathing room.
					 */
					let remainingSpace = Math.max(0, donatedSpace - 5);

					/*
					 * ---------------------------------------------------------
					 * 7. Redistribute donated space.
					 * ---------------------------------------------------------
					 */

					while (remainingSpace > 0.1 && receivers.length > 0) {
						const needs = {};

						let totalNeed = 0;

						receivers.forEach((field) => {
							needs[field] = Math.max(0, natural[field] - result[field]);

							totalNeed += needs[field];
						});

						if (totalNeed <= 0) break;

						let used = 0;

						receivers.forEach((field) => {
							if (needs[field] <= 0) return;

							const share = remainingSpace * (needs[field] / totalNeed);

							const extra = Math.min(share, needs[field]);

							result[field] += extra;
							used += extra;
						});

						remainingSpace -= used;

						receivers = receivers.filter(
							(field) => natural[field] > result[field] + 0.1
						);

						if (used <= 0.1) break;
					}

					/*
					 * ---------------------------------------------------------
					 * 8. Final safety:
					 * ---------------------------------------------------------
					 */

					const totalResult = result.company + result.jobType + result.location;

					if (totalResult > availableWidth) {
						const scale = availableWidth / totalResult;

						fields.forEach((field) => {
							result[field] *= scale;
						});
					}

					/*
					 * ---------------------------------------------------------
					 * 9. Save styles.
					 * ---------------------------------------------------------
					 */

					widths[key] = {};

					fields.forEach((field) => {
						widths[key][field] = {
							width: `${Math.floor(result[field])}px`,
							minWidth: "0",
							flex: "0 0 auto",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						};
					});
				});

				this.jobSubtitleWidths = widths;
			});
		},

		subtitleKey(card, index) {
			const raw =
				card.id !== undefined && card.id !== null && card.id !== ""
					? card.id
					: card.jobID || card._id || "job";
			return `${raw}__${index}`;
		},

		jobFieldStyle(card, index, field) {
			const entry = this.jobSubtitleWidths[this.subtitleKey(card, index)];
			return (entry && entry[field]) || undefined;
		},
		async fetchProgressHub() {
			try {
				this.isLoading = true;
				const res = await getProgressHubData();
				if (res && res.data && res.data.type === "Success") {
					this.cards = Array.isArray(res.data.data) ? res.data.data : [];
					// API succeeded but returned no data — don't render the section
					if (this.cards.length === 0) {
						this.hasError = true;
					} else {
						this.hasError = false;
					}
				} else {
					this.hasError = true;
				}
			} catch (err) {
				console.error("ProgressHub fetch error:", err);
				this.hasError = true;
			} finally {
				this.isLoading = false;

				this.$nextTick(() => {
					this.calculateJobSubtitleWidths();
					this.updateScrollArrows();
				});
			}
			if (this.hasError) {
				this.$emit("load-error");
			}
		},
		cardIcon(type) {
			const map = {
				job: require("../../../assets/NewDashboard/progresshub/jobapplication.svg"),
				assessment: require("../../../assets/NewDashboard/progresshub/asmnticon.svg"),
				course: require("../../../assets/NewDashboard/progresshub/courseicon.svg"),
			};
			return map[type] || map["job"];
		},
		cardTypeLabel(type) {
			const map = {
				job: "Job Application",
				assessment: "Online Assessment",
				course: "Course",
			};
			return map[type] || type;
		},
		formatDuration(minutes) {
			if (!minutes || minutes <= 0) return "";
			if (minutes > 59) {
				const hrs = Math.floor(minutes / 60);
				const mins = minutes % 60;
				return mins ? `${hrs} hr ${mins} min` : `${hrs} hr`;
			}
			return `${minutes} mins`;
		},
		formatEventDate(dateStr) {
			if (!dateStr) return "";
			const d = new Date(dateStr);
			const day = String(d.getDate()).padStart(2, "0");
			const month = d.toLocaleString("en-US", { month: "short" });
			const year = d.getFullYear();
			const hours = d.getHours();
			const mins = String(d.getMinutes()).padStart(2, "0");
			const ampm = hours >= 12 ? "PM" : "AM";
			const h = hours % 12 || 12;
			return `${day}/${month}/${year} | ${String(h).padStart(
				2,
				"0"
			)}:${mins} ${ampm}`;
		},
		resolveCardId(card) {
			if (card.type === "job") return card.jobID || card.id || card._id;
			if (card.type === "course")
				return card.courseID || card.id || card._id || card.referenceID;
			if (card.type === "assessment") return card.testID || card.id || card._id;
			return null;
		},
		trackCardViewed(card) {
			if (!card) return;
			const spec = CARD_EVENT[card.type];
			if (!spec) return;
			const rawId = this.resolveCardId(card);
			this.trackOnce(spec.key, rawId, spec.payload(rawId));
		},
		onCardClick(card) {
			if (!card) return;
			this.trackCardViewed(card);

			if (card.type === "job") {
				// Prefer the ctaLink returned by the API (e.g. "/job/progress?jobID=...").
				if (card.ctaLink) {
					if (/^https?:\/\//i.test(card.ctaLink)) {
						window.open(card.ctaLink, "_self");
					} else {
						this.$router.push(card.ctaLink).catch(() => {});
					}
					return;
				}
				const jobId = this.resolveCardId(card);
				if (jobId) {
					this.$router
						.push({
							name: "JobSearch",
							query: { jobID: jobId },
						})
						.catch(() => {});
				}
			} else if (card.type === "course") {
				const courseId = this.resolveCardId(card);
				const courseTitle = encodeURIComponent(
					card.title || card.courseName || ""
				);
				if (courseId) {
					const link = `${config.trainingBase}/coursedetail/${courseId}?courseName=${courseTitle}`;
					window.open(link, "_self");
					return;
				}
				if (card.ctaLink) {
					if (/^https?:\/\//i.test(card.ctaLink)) {
						window.open(card.ctaLink, "_self");
					} else {
						window.open(
							`${config.trainingBase}${
								card.ctaLink.startsWith("/") ? "" : "/"
							}${card.ctaLink}`,
							"_self"
						);
					}
				}
			} else if (card.type === "assessment") {
				// Open that specific assessment's details via getAssessmentDetail
				// (the per-assessment API), mirroring the ProgressHub reference flow.
				this.openAssessmentDetails(card);
			} else {
				if (card.ctaLink) {
					if (/^https?:\/\//i.test(card.ctaLink)) {
						window.open(card.ctaLink, "_self");
					} else {
						this.$router.push(card.ctaLink).catch(() => {});
					}
				}
			}
		},
		async openAssessmentDetails(card) {
			const testID = card.testID || card.id || card._id;
			if (!testID) return;
			try {
				const params = {
					testID,
					...(card.groupID && { groupID: card.groupID }),
				};
				const res = await getAssessmentDetail(params);
				if (res.status === 200) {
					this.selectedAssessment = res.data.data;
					if (this.isMobile) {
						this.$store.commit(
							"candidateVUEX/addSelectAssess",
							this.selectedAssessment
						);
						this.$router
							.push({
								name: "mobAssessment",
								query: { public: false, from: "home" },
							})
							.catch(() => {});
					} else {
						this.showTestDetails = true;
					}
				}
			} catch (e) {
				this.$router.push(`/assessment/${testID}/details`).catch(() => {});
			}
		},
		updateScrollArrows() {
			const container = this.$refs.cardsScroll;
			if (!container) {
				this.canScrollLeft = false;
				this.canScrollRight = false;
				return;
			}
			const tolerance = 2;
			this.canScrollLeft = container.scrollLeft > tolerance;
			this.canScrollRight =
				container.scrollLeft + container.clientWidth <
				container.scrollWidth - tolerance;
		},
		onCardsScroll() {
			this.updateScrollArrows();
			const container = this.$refs.cardsScroll;
			if (!container) return;
			const card = container.querySelector(".ph-maincard");
			if (!card) return;
			const step = card.getBoundingClientRect().width + 16;
			const newIndex = Math.round(container.scrollLeft / step);
			if (newIndex !== this.activeCardIndex) {
				this.activeCardIndex = newIndex;
			}
		},
		scrollCards(direction) {
			const container = this.$refs.cardsScroll;
			if (!container) return;
			const card = container.querySelector(".ph-maincard");
			if (!card) return;
			const step = card.getBoundingClientRect().width + 16;
			container.scrollBy({
				left: direction === "right" ? step : -step,
				behavior: "smooth",
			});
		},
	},
};
</script>

<style scoped>
.color-64748B {
	color: #64748b;
}
.color-0F172A {
	color: #0f172a;
}
.ph-maincard {
	padding: 12px;
	width: clamp(220px, calc(100vw - 72px), 305px);
	min-height: 115px;
	height: auto;
	background: linear-gradient(134.23deg, #ffffff 5.69%, #fefefe 96.31%);
	border: 1.5px solid rgba(82, 166, 255, 0.21);
	border-radius: 24px;
	position: relative;
	padding: 13.5px;
	flex-shrink: 0;
}
.ph-maincard-tag {
	padding: 2px 8px;
	height: 22px;
	border-radius: 4px;
	position: absolute;
	top: -12px;
	right: -5px;
	font-family: "Inter", sans-serif;
	font-weight: 500;
	font-size: 12px;
	/* white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 280px; */
}
.ph-othertags {
	width: fit-content !important;
	height: -moz-fit-content !important;
	height: fit-content !important;
	top: -15px !important;
	padding: 2px 4px;
}
.ph-jobapllicationtag {
	background: #faf5ff;
	border: 1px solid #f3e8ff;
	color: rgba(126, 34, 206, 0.8);
}
.ph-coursestag {
	background: #fff9f1;
	border: 1px solid #ffeace;
	color: #c04800;
}
.gap-10px {
	gap: 10px;
}
.progressicon-shadow {
	width: 40px;
	height: 40px;
	box-shadow: 0px -4.56294px 11.4073px rgba(255, 255, 255, 0.9),
		0px 9.12587px 22.8147px rgba(0, 0, 0, 0.1);
	border-radius: 8px;
}
.mt-8px {
	margin-top: 8px;
}
.ph-subtitle {
	display: flex;
	align-items: baseline;
	gap: 4px;
	overflow: hidden;
}

.ph-subtitle-item {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.ph-subtitle-bullet {
	flex-shrink: 0;
}
.ph-snap-scroll {
	scroll-snap-type: x mandatory;
}
.ph-snap-item {
	scroll-snap-align: start;
}
.ph-empty-state {
	padding: 24px;
	text-align: center;
	width: 100%;
}

/* Skeleton */
.ph-skeleton {
	background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
	background-size: 200% 100%;
	animation: shimmer 1.4s infinite;
	border-radius: 6px;
}
.ph-skeleton-tag {
	width: 140px;
	height: 18px;
	position: absolute;
	top: -10px;
	right: 0;
}
.ph-skeleton-icon {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	flex-shrink: 0;
}
.ph-skeleton-label {
	width: 90px;
	height: 12px;
}
.ph-skeleton-title {
	width: 180px;
	height: 14px;
}
.ph-skeleton-sub {
	width: 140px;
	height: 12px;
}
@keyframes shimmer {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

/* sliding dot indicator */
.ph-dot-viewport {
	width: 51px;
	overflow: hidden;
	margin: 12px auto 0;
	display: flex;
	align-items: center;
	height: 8px;
}
.ph-dot-track {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
	transition: transform 0.35s ease;
}
.ph-dot {
	border-radius: 100px;
	flex-shrink: 0;
	transition: width 0.35s ease, min-width 0.35s ease, background 0.35s ease;
}
.ph-dot--small {
	width: 8px;
	height: 8px;
	background: #cecece;
	min-width: 8px;
}
.ph-dot--active {
	width: 19px;
	min-width: 19px;
	height: 8px;
	background: #333333;
}
.pt-20px {
	padding-top: 20px;
}
@media only screen and (max-width: 1028px) {
	.ph-maincard {
		width: 241px;
		width: clamp(200px, calc(100vw - 72px), 241px);
		min-height: 146px;
		height: auto;
		background: linear-gradient(134.23deg, #ffffff 5.69%, #ebf0fc 96.31%);
		border: 1.5px solid #e9e9e9;
		border-radius: 24px;
	}
	.ph-maincard-tag {
		width: fit-content;
		max-width: calc(100% + 10px);
		height: fit-content;
		top: -31px;
		padding: 2px 4px;
		white-space: normal;
		line-height: 1.3;
	}
	.mobpt-30px {
		padding-top: 30px;
	}
}
</style>
